import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lightning, MagnifyingGlass, X, CircleNotch } from "@phosphor-icons/react";

/**
 * Renders a small "Insertar plantilla" button that opens a dropdown of
 * available message templates. When the user picks one, the template's
 * `content` is passed through `renderPlaceholders(ctx)` and forwarded to
 * the parent via `onSelect(rendered)`.
 *
 * Placeholders supported: {customer_name}, {agent_name}, {branch}, {date}, {time}.
 */
const PLACEHOLDER_RE = /\{(\w+)\}/g;

function renderPlaceholders(text, ctx) {
  if (!text) return "";
  return text.replace(PLACEHOLDER_RE, (m, key) => {
    if (ctx && key in ctx && ctx[key]) return ctx[key];
    return m; // leave unresolved so the agent can fill it in
  });
}

export default function MessageTemplatesPicker({ onSelect, ctx = {}, channel, className = "", lang = "es" }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = new URLSearchParams({ enabled_only: "true" });
    if (channel) params.set("channel", channel);
    api.get(`/message-templates?${params.toString()}`)
      .then((r) => setTemplates(r.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open, channel]);

  // Only show templates the agent can insert (is_agent_macro true or absent).
  const macroFiltered = templates.filter((t) => t.is_agent_macro !== false);
  const pickContent = (t) => (lang === "en" && t.content_en) ? t.content_en : t.content;
  const filtered = q
    ? macroFiltered.filter((t) => (t.title + " " + pickContent(t)).toLowerCase().includes(q.toLowerCase()))
    : macroFiltered;

  const grouped = filtered.reduce((acc, t) => {
    (acc[t.category || "general"] = acc[t.category || "general"] || []).push(t);
    return acc;
  }, {});

  return (
    <div className={`relative inline-block ${className}`}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        data-testid="templates-picker-toggle"
        className="gap-1 h-8"
      >
        <Lightning size={12} weight="fill" />
        Plantillas
      </Button>

      {open && (
        <div
          className="absolute bottom-full mb-2 right-0 w-80 max-h-[400px] rounded-md border border-border bg-card shadow-xl z-50 overflow-hidden flex flex-col"
          data-testid="templates-picker-panel"
        >
          <div className="p-2 border-b border-border flex items-center gap-1.5">
            <MagnifyingGlass size={12} className="text-muted-foreground shrink-0 ml-1" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar plantilla…"
              className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
              data-testid="templates-picker-search"
              autoFocus
            />
            <button
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-muted rounded"
              aria-label="Cerrar"
            ><X size={12}/></button>
          </div>
          <div className="flex-1 overflow-auto">
            {loading && (
              <div className="p-4 text-xs text-muted-foreground flex items-center gap-1.5">
                <CircleNotch size={12} className="animate-spin" /> Cargando…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-4 text-xs text-muted-foreground text-center">
                Sin plantillas. Un administrador puede crearlas en <b>Ajustes</b>.
              </div>
            )}
            {!loading && Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-muted-foreground bg-muted/30 sticky top-0">
                  {cat}
                </div>
                {items.map((t) => {
                  const content = pickContent(t);
                  return (
                  <button
                    key={t.id}
                    onClick={() => {
                      onSelect(renderPlaceholders(content, ctx));
                      setOpen(false);
                      setQ("");
                    }}
                    data-testid={`template-item-${t.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-muted transition-colors border-t border-border first:border-t-0"
                  >
                    <div className="text-xs font-medium flex items-center gap-1.5">
                      {t.title}
                      {t.source === "bdc_seed" && (
                        <span className="text-[8px] uppercase bg-amber-500/90 text-white rounded px-1 py-0.5 leading-none">BDC</span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">
                      {content}
                    </div>
                  </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
